import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push, replace } from 'redux-router';
import {
  IAddress,
  IOrganizationAddress,
  IOrganizationSearch,
  IProductInfo,
  IReferer,
  ISessionInfo,
  IState,
  IUserInfo,
  Push,
  Replace
} from '../CustomTypes';
import { svgPaymentCard } from '../icons';
import { Button, InputField, Name } from '../input-components/InputComponents';
import { FormEvent } from 'react';
import { apiStatuses } from '../actions/Actions';
import { createB64data } from '../fastflowUtils';
import './CompanyPaysPage.scss';
import {
  validateAtLeastOneCharacter,
  validateCity,
  validateCompanyName,
  validateName,
  validateOrgnumber,
  validateZipCode
} from '../validators';
import OrganizationSearch from '../input-components/OrganizationSearch';
import { sccPulseTrackPageView } from '../PulseUtils';

export interface IStateProps {
  __rct?: string;
  address: IAddress;
  b64data?: string;
  basename?: string;
  error?: string;
  paymentType: string;
  productInfo: IProductInfo;
  referer: IReferer;
  sessionInfo: ISessionInfo;
  userInfo: IUserInfo;
  organizationData: IOrganizationSearch;
}

export interface IDispatchProps {
  push?: Push;
  replace?: Replace;
}

export interface IProps extends IDispatchProps, IStateProps {}

export interface IInternalState {
  companyContactEmail: string;
  companyContactFirstName: string;
  companyContactLastName: string;
  companyContactPhone: string;
  companyName: string;
  formSubmitted: boolean;
  reference: string;
  referenceEdited: boolean;
  invoiceStreet: string;
  invoiceCity: string;
  invoiceZipCode: string;
}

const name = (userInfo: IUserInfo): string => {
  return userInfo
    ? `${[userInfo.firstName || '', userInfo.lastName || '']
        .filter(Boolean)
        .join(' ')}`
    : '';
};

export class CompanyPaysPage extends React.Component<IProps, IInternalState> {
  constructor(props: IProps) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = {
      companyContactEmail: '',
      companyContactFirstName: '',
      companyContactLastName: '',
      companyContactPhone: '',
      companyName: '',
      formSubmitted: false,
      invoiceCity: '',
      invoiceStreet: '',
      invoiceZipCode: '',
      reference: name(props.userInfo),
      referenceEdited: false
    };
  }

  public componentDidMount(): void {
    sccPulseTrackPageView(this.props.productInfo, {}, 'companypays');
  }

  public componentWillReceiveProps(nextProps: Readonly<IProps>): void {
    const userName = name(nextProps.userInfo);

    const { reference, referenceEdited } = this.state;
    if (reference === '' && !referenceEdited && userName) {
      this.setState({ reference: userName, referenceEdited: true });
    }
  }

  public render() {
    const {
      companyContactEmail,
      companyContactFirstName,
      companyContactLastName,
      companyContactPhone,
      formSubmitted,
      reference
    } = this.state;

    return (
      <div className="container companyPays">
        <h1>{svgPaymentCard()}</h1>
        <h1>Send faktura til arbeidsgiver</h1>
        <a href="https://www.proff.no/" target="_blank">
          Finn ditt org.nr. på proff.no
        </a>
        <form onSubmit={this.onSubmit}>
          <div className="row">
            <div className="col-xs-12">
              <OrganizationSearch formSubmitted={formSubmitted} />
            </div>
            <div className="col-xs-12">
              <InputField
                label="Firmanavn"
                placeholder="Skriv inn ditt firmanavn her"
                value={this.getCompanyName()}
                type={'text'}
                onChange={e =>
                  this.setState({ companyName: e.currentTarget.value })
                }
                validationErrorMessage="Vennligst oppgi firmanavn"
                validator={validateCompanyName}
                formSubmitted={formSubmitted}
              />
            </div>
            <div className="col-xs-12">
              <Name spacing={true} />
            </div>
            <div className="col-xs-12">
              <InputField
                label="Fakturaadresse"
                placeholder="Skriv inn din fakturaadresse her"
                value={this.getInvoiceStreet()}
                validator={validateAtLeastOneCharacter}
                onChange={e =>
                  this.setState({ invoiceStreet: e.currentTarget.value })
                }
                validationErrorMessage="Vennligst oppgi fakturaadresse"
                formSubmitted={formSubmitted}
              />
            </div>

            <div className="col-xs-6">
              <InputField
                label="Postnr"
                placeholder="Skriv inn ditt postnummer her"
                value={this.getInvoiceZipCode()}
                type="tel"
                validator={validateZipCode}
                onChange={e =>
                  this.setState({ invoiceZipCode: e.currentTarget.value })
                }
                validationErrorMessage="Vennligst oppgi postnr"
                formSubmitted={formSubmitted}
              />
            </div>
            <div className="col-xs-6">
              <InputField
                label="Poststed"
                placeholder="Skriv inn ditt poststed her"
                value={this.getInvoiceCity()}
                validator={validateCity}
                onChange={e =>
                  this.setState({ invoiceCity: e.currentTarget.value })
                }
                validationErrorMessage="Vennligst oppgi poststed"
                formSubmitted={formSubmitted}
              />
            </div>

            {false && (
              <div className="col-xs-12 validation-messages">
                <div className="alert alert-error">
                  Vennligst oppgi både postnummer og poststed
                </div>
              </div>
            )}

            <div className="col-xs-12">
              <InputField
                label="Referanse / PO nummer"
                placeholder="Skriv inn din referanse / PO nummer her"
                value={reference}
                type={'text'}
                onChange={e =>
                  this.setState({
                    reference: e.currentTarget.value,
                    referenceEdited: true
                  })
                }
                validationErrorMessage="Vennligst oppgi referanse"
                validator={validateAtLeastOneCharacter}
                formSubmitted={formSubmitted}
              />
            </div>

            <div className="col-xs-12">
              <h3>Arbeidsgivers kontaktperson (valgfritt)</h3>
              <div className="row">
                <div className="inputField form-group">
                  <div className="col-xs-6">
                    <InputField
                      label="Epost"
                      placeholder="Skriv inn eposten til kontaktperson"
                      value={companyContactEmail}
                      onChange={e =>
                        this.setState({
                          companyContactEmail: e.currentTarget.value
                        })
                      }
                      formSubmitted={formSubmitted}
                    />
                  </div>
                  <div className="col-xs-6">
                    <InputField
                      label="Tlf nr."
                      placeholder="Skriv inn tlf nummer til kontaktperson"
                      value={companyContactPhone}
                      onChange={e =>
                        this.setState({
                          companyContactPhone: e.currentTarget.value
                        })
                      }
                      formSubmitted={formSubmitted}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="inputField form-group">
                  <div className="col-xs-6">
                    <InputField
                      label="Fornavn"
                      placeholder="Skriv inn fornavnet til kontaktperson"
                      value={companyContactFirstName}
                      onChange={e =>
                        this.setState({
                          companyContactFirstName: e.currentTarget.value
                        })
                      }
                      formSubmitted={formSubmitted}
                    />
                  </div>
                  <div className="col-xs-6">
                    <InputField
                      label="Etternavn"
                      placeholder="Skriv inn etternavnet til kontaktperson"
                      value={companyContactLastName}
                      onChange={e =>
                        this.setState({
                          companyContactLastName: e.currentTarget.value
                        })
                      }
                      formSubmitted={formSubmitted}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Button type="submit">Fullfør bestillingen</Button>
        </form>
      </div>
    );
  }

  private getCompanyName = () => {
    const { organizationData } = this.props;
    return (
      this.state.invoiceStreet ||
      (organizationData.data && organizationData.data.navn)
    );
  };

  private getInvoiceStreet = () => {
    const address = this.getOrganizationAddress();
    return (
      this.state.invoiceStreet ||
      (address &&
        address.adresse &&
        address.adresse.length > 0 &&
        address.adresse[0])
    );
  };

  private getInvoiceZipCode = () => {
    const address = this.getOrganizationAddress();
    return this.state.invoiceZipCode || (address && address.postnummer);
  };

  private getInvoiceCity = () => {
    const address = this.getOrganizationAddress();
    return this.state.invoiceCity || (address && address.poststed);
  };

  private getOrganizationAddress = () => {
    const { organizationData } = this.props;
    return (
      (organizationData.data &&
        (organizationData.data.postadresse ||
          organizationData.data.forretningsadresse)) ||
      ({} as IOrganizationAddress)
    );
  };

  private onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    this.setState({ formSubmitted: true });
    const {
      companyContactEmail,
      companyContactFirstName,
      companyContactLastName,
      companyContactPhone,
      reference
    } = this.state;
    const { organizationData } = this.props;
    const { firstName, lastName } = this.props.userInfo;

    const organizationNumber = organizationData.input;
    const companyName = this.getCompanyName();
    const invoiceStreet = this.getInvoiceStreet();
    const invoiceZipCode = this.getInvoiceZipCode();
    const invoiceCity = this.getInvoiceCity();

    if (
      !validateOrgnumber(organizationNumber) ||
      !validateCompanyName(companyName) ||
      !validateName(firstName) ||
      !validateName(lastName) ||
      !validateAtLeastOneCharacter(invoiceStreet) ||
      !validateZipCode(invoiceZipCode) ||
      !validateCity(invoiceCity) ||
      !validateAtLeastOneCharacter(reference)
    ) {
      return;
    }

    let rctParam = '';
    if (this.props.__rct) {
      rctParam += `&__rct=${this.props.__rct}`;
    }

    this.props.push(
      `/products/${
        this.props.productInfo.productId
      }/placeorder?${this.getSpidIdAndSigURIParamsIfInUrl()}${this.createB64data()}&paymentType=companyPays` +
        `&organizationNumber=${encodeURIComponent(
          organizationNumber
        )}&companyName=${encodeURIComponent(
          companyName
        )}&invoiceStreet=${encodeURIComponent(
          invoiceStreet
        )}&invoiceCity=${encodeURIComponent(
          invoiceCity
        )}&invoiceZipCode=${encodeURIComponent(
          invoiceZipCode
        )}&reference=${encodeURIComponent(
          reference
        )}&companyContactEmail=${encodeURIComponent(
          companyContactEmail
        )}&companyContactFirstName=${encodeURIComponent(
          companyContactFirstName
        )}&companyContactLastName=${encodeURIComponent(
          companyContactLastName
        )}&companyContactPhone=${encodeURIComponent(
          companyContactPhone
        )}${rctParam}`
    );
  }

  private getSpidIdAndSigURIParamsIfInUrl() {
    if (this.props.sessionInfo.apiStatus === apiStatuses.ERROR) {
      // Not logged in, so save sig/spidId in case of reload
      const { spidId, sig } = this.props.userInfo;
      return `&spidId=${spidId}&sig=${sig}`;
    }
    return '';
  }

  private createB64data() {
    if (this.props.b64data) {
      return `&b64data=${encodeURIComponent(this.props.b64data)}`;
    }

    const b64data = createB64data(
      this.props.address,
      this.props.referer,
      this.props.userInfo.phoneNumber,
      false,
      this.props.userInfo.email
    );
    return `&b64data=${encodeURIComponent(b64data)}`;
  }
}

export default connect<IStateProps, IDispatchProps, {}>(
  (state: IState) =>
    ({
      __rct: state.router.location.query._rct,
      address: state.address,
      b64data: state.router.location.query.b64data,
      basename: state.router.location.basename,
      error: state.router.location.query.error,
      organizationData: state.organizationData,
      paymentType: (state.router.params as any).paymentType,
      productInfo: state.productInfo,
      referer: state.referer,
      sessionInfo: state.sessionInfo,
      userInfo: state.userInfo
    } as IStateProps),
  dispatch => bindActionCreators({ replace, push }, dispatch)
)(CompanyPaysPage);



// WEBPACK FOOTER //
// ./src/page-components/CompanyPaysPage.tsx